import { faCalculator } from "@fortawesome/free-solid-svg-icons";
import Form from "components/common/form/Form";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import DateInput from "components/common/form/inputs/date/DateInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import FormActions from "components/common/form/partials/FormActions";
import PersonDropdown from "components/entities/app/person/partials/inputs/PersonDropdown";
import WalletDropdown from "components/entities/app/person/wallet/WalletDropdown";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import { graph } from "config/axios-context";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fruitCategoriesIdNameQuery, fruitsIdNameQuery } from "types/agriculture/sale-types";
import { IdName } from "types/common-types";
import { DropdownInputPropsType } from "types/form-types";
import { FruitCategoriesIdNameQuery, FruitCategoriesIdNameQueryVariables, FruitsIdNameQuery, FruitsIdNameQueryVariables, SaleTypes } from "types/graphql";
import { PersonType } from "types/person-types";
import { SeasonType } from "types/season-type";
import useAppContext from "utilities/hooks/useAppContext";

type SaleFormInputsProps = {
    onSubmit: (model: SaleType, e: React.FormEvent<HTMLFormElement>) => Promise<void>,
    name: string,
    isTemplate?: boolean,
    model?: SaleType
}

export type SaleType = {
    id: number,
    season?: SeasonType,
    name: string,
    date?: Date,
    person?: PersonType,
    fruit?: IdName,
    kg?: number | string,
    pricePerKg?: number | string,
    total?: number | string,
    chamber?: boolean,
    cartQuantity?: number | string | null,
    fruitCategory?: IdName,
    type?: SaleTypes,
    note?: string,
    wallet?: {
        id: number,
        name: string,
        balance?: number | null
    } | null
}

const SaleFormInputs = (props: SaleFormInputsProps) => {
    const [disableInputs, setDisableInputs] = useState(false);
    const context = useAppContext();
    const [t] = useTranslation();
    const [model, setModel] = useState<SaleType>(props.model || {
        id: 0,
        name: '',
        date: new Date(),
        fruit: {
            id: 1,
            name: 'Mandarina'
        },
        kg: '',
        pricePerKg: '',
        total: '',
        chamber: false,
        cartQuantity: '',
        type: SaleTypes.Wholesale,
        note: ''
    });

    let fruits: IdName[] = [];

    const onChangeValue = (m: Partial<SaleType>) => {
        setModel({
            ...model,
            ...m
        });
    }

    // #region configs
    const fruitDropdownSettings: DropdownInputPropsType<IdName> = {
        dataSource: {
            request: async () => {
                if(fruits.length) return fruits;

                const response = await graph<FruitsIdNameQuery, FruitsIdNameQueryVariables>({
                    query: fruitsIdNameQuery
                });

                fruits = response.data!.fruits!.items! as IdName[];
                return fruits;
            },
            search: 'switch'
        },
        getItemId: i => i.id,
        view: {
            inputView: i => i?.name
        },
        required: !props.isTemplate,
        label: 'fields.fruit.name',
        name: "agriculture-sale-form-page-fruit",
        onChange: v => onChangeValue({ fruit: v })
    }

    const saleTypeDropdownSettings: DropdownInputPropsType<SaleTypes> = {
        dataSource: {
            data: [
                SaleTypes.Wholesale,
                SaleTypes.Retail
            ],
            search: 'off'
        },
        getItemId: i => i,
        view: {
            inputView: i => i
        },
        required: !props.isTemplate,
        label: 'fields.saleType.name',
        name: "agriculture-sale-form-page-saleType",
        onChange: v => onChangeValue({ type: v })
    }

    const fruitCategoryDropdownSettings: DropdownInputPropsType<IdName> = {
        dataSource: {
            request: async params => {
                if(!model.fruit || !model.fruit.id) {
                    return [{id: -1, name: t('form.agriculture.sale.error.fruitCategoryInput')}]
                }

                const request = await graph<FruitCategoriesIdNameQuery, FruitCategoriesIdNameQueryVariables>({
                    query: fruitCategoriesIdNameQuery,
                    variables: {
                        where: {
                            fruitId: {
                                eq: model.fruit.id
                            }
                        }
                    }
                })

                if(!request.success) {
                    context.notify('error', 'error.loading-data');
                    throw request.errors;
                }
                
                return request.data?.fruitCategories?.items as IdName[];
            },
            search: 'switch'
        },
        getItemId: i => i.id,
        view: {
            inputView: i => i?.name ? t('agriculture.category.' + i.name.toLowerCase()) : ''
        },
        required: !props.isTemplate,
        label: 'fields.category.name',
        name: "fruitCategory",
        onChange: v => onChangeValue({ fruitCategory: v }), 
        customErrors: [{
            message: 'InvalidInput',
            name: 'InvalidInput',
            validate(v) {
                return !v || v?.id > 0 ? true : false;
            },
        }]
    }

    const calculate = (force?: boolean) => {
        if (!force && Number(model.total)) return;
        model.total = (Number(model.kg) || 0) * (Number(model.pricePerKg) || 0);
        model.total = model.total ? model.total.toFixed(2) : model.total;
        onChangeValue({ total: model.total });
    }
    // #endregion configs

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        setDisableInputs(true);
        
        try {
            props.onSubmit(model, e);
        } catch (error) {
            context.notify('error', 'form.submit.error');
            throw error;
        } finally {
            setDisableInputs(false);
        }
    }

    return (
        <Form name={props.name} onSubmit={onSubmit}>
            <fieldset>
                <SeasonDropdown
                    name="agriculture-sale-form-page-season"
                    required={!props.isTemplate}
                    onChange={v => onChangeValue({ season: v })}
                    value={model.season}
                    label="fields.season.name"
                />
                <Input
                    name="agriculture-sale-form-page-name"
                    required
                    value={model.name}
                    label="fields.name.name"
                    onChange={v => onChangeValue({ name: v })}
                    maxLength={50}
                />
                <DateInput
                    name="agriculture-sale-form-page-date"
                    required={!props.isTemplate}
                    value={model.date}
                    label="fields.date.name"
                    onChange={v => onChangeValue({ date: v })}
                />
                <PersonDropdown
                    name="agriculture-sale-form-page-person"
                    required={!props.isTemplate}
                    value={model.person}
                    addPerson
                    label="fields.person.name"
                    onChange={v => onChangeValue({ person: v, wallet: undefined })}
                />
                <WalletDropdown
                    name="agriculture-sale-form-page-wallet"
                    personId={model.person?.id}
                    value={model.wallet || undefined}
                    onChange={v => onChangeValue({ wallet: v })}
                />
            </fieldset>
            <fieldset>
                <DropdownInput
                    {...fruitDropdownSettings}
                    value={model.fruit}
                />

                <Input
                    name="kg"
                    type="number"
                    required={!props.isTemplate}
                    value={model.kg?.toString()}
                    onChange={v => onChangeValue({ kg: v })}
                    label="fields.kg.name"
                />

                <Input
                    name="pricePerKg"
                    type="number"
                    required={!props.isTemplate}
                    value={model.pricePerKg?.toString()}
                    onChange={v => onChangeValue({ pricePerKg: v })}
                    label="fields.price-per-kg.name"
                />

                <Input
                    name="total"
                    type="number"
                    required={!props.isTemplate}
                    value={model.total?.toString()}
                    onChange={v => onChangeValue({ total: v })}
                    label="fields.total.name"
                    onFocus={e => calculate(false)}
                    suffix={{ icon: faCalculator, onClick: () => calculate(true), name: t('common.calculate') }}
                />
            </fieldset>
            <fieldset>
                <DropdownInput
                    {...fruitCategoryDropdownSettings}
                    value={model.fruitCategory}
                />
                <DropdownInput
                    {...saleTypeDropdownSettings}
                    value={model.type}
                />
                <Input
                    name="cartQuantity"
                    type="number"
                    required={!props.isTemplate}
                    value={model.cartQuantity?.toString()}
                    onChange={v => onChangeValue({ cartQuantity: v })}
                    label="fields.cart-quantity.name"
                />
                <CheckboxInput
                    name='chamber'
                    label='fields.chamber.name'
                    value={model.chamber}
                    onChange={v => onChangeValue({ chamber: v })}
                />
            </fieldset>
            <fieldset>
                <Textarea
                    name="note"
                    label='fields.note.name'
                    onChange={v => onChangeValue({ note: v })}
                    value={model.note}
                />
            </fieldset>
            <FormActions disableInputs={disableInputs} />
        </Form>
    );
}

export default SaleFormInputs;
